import { switchMap } from "rxjs/operators";
import { Observable, Subject, BehaviorSubject } from "rxjs";
import { Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { FileExportService } from "./file-export.service";
import {
  StatisticsFilters,
  IAlertStatistics,
  IEmaacStatistics,
  CareRecipientCard,
  CareRecipientStatistics,
  ICareRecipientCardUserLimit,
  ICareRecipientCardNote,
  IActiveUsersStats,
  IActiveUsersByWeekday,
  IActiveUsersByHour,
  IResponseTimeStatistics
} from "app/models/statistics.model";
import { IAlarmReasonTranslations } from "app/models/alarm-reasons.model";

@Injectable({
  providedIn: "root",
})
export class StatisticsService {
  constructor(private api: ApiService, private fileExporter: FileExportService) {
    this.alertStatistics$ = this.fetchAlertStatistics$.pipe(
      switchMap(() => this.fetchAlertStatistics())
    );

    this.emaacStatistics$ = this.fetchEmaacStatistics$.pipe(
      switchMap(() => this.fetchEmaacStatistics())
    );
    this.careRecipientCards$ = this.fetchCareRecipientCards$.pipe(
      switchMap(() => this.fetchCareRecipientCards())
    );
    this.activeUsersStatistics$ = this.fetchActiveUsersStats$.pipe(
      switchMap(() => this.fetchActiveUsersStatistics())
    );
  }

  page: number = 0;
  pageSize = 1000;

  filters: StatisticsFilters = new StatisticsFilters();

  fetchAlertStatistics$ = new Subject<void>();
  fetchEmaacStatistics$ = new Subject<void>();
  fetchCareRecipientCards$ = new Subject<void>();
  fetchActiveUsersStats$ = new Subject<void>();

  alertStatistics$: Observable<IAlertStatistics>;
  emaacStatistics$: Observable<IEmaacStatistics>;
  careRecipientCards$: Observable<CareRecipientStatistics[][]>;
  activeUsersStatistics$: Observable<IActiveUsersStats>;

  alertStatistics: IAlertStatistics;
  emaacStatistics: IEmaacStatistics;
  careRecipientCards: CareRecipientStatistics[][];
  activeUsersStatistics: IActiveUsersStats;

  removedCareRecipientCardId$ = new BehaviorSubject<string>("");

  alertsTabDataLoaded: boolean = false;
  alarmCenterTabDataLoaded: boolean = false;
  careRecipientCardsLoaded: boolean = false;
  activeUsersTabDataLoaded: boolean = false;

  alertsTabPagingComplete: boolean = false;
  alarmCenterTabPagingComplete: boolean = false;

  alarmReasonTranslations: IAlarmReasonTranslations | null = null;

  careRecipientTabResultMsg: string = "";

  activeUsersPerWeekday: IActiveUsersByWeekday
  activeUsersPerHour: IActiveUsersByHour;

  getStatisticsTabData(tab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers"): void {
    switch (tab) {
      case "alerts":
        this.fetchAlertStatistics$.next();
        break;
      case "alarmCenter":
        this.fetchEmaacStatistics$.next();
        break;
      case "careRecipients":
        this.fetchCareRecipientCards$.next();
        break;
      case "activeUsers":
        this.fetchActiveUsersStats$.next();
        break;
    }
  }

  fetchAlertStatistics(): Observable<IAlertStatistics> {
    this.alertsTabDataLoaded = false;
    this.alertsTabPagingComplete = false;
    // Invalidate the cache so a failed fetch cannot leave stale data exportable.
    this.alertStatistics = undefined;
    const stats = this.api.getAlertStatistics(this.filters, this.page);
    this.page++;
    return stats;
  }

  fetchEmaacStatistics(): Observable<IEmaacStatistics> {
    this.alarmCenterTabDataLoaded = false;
    this.alarmCenterTabPagingComplete = false;
    // Invalidate the cache so a failed fetch cannot leave stale data exportable.
    this.emaacStatistics = undefined;
    const stats = this.api.getAlarmCenterStatistics(this.filters, this.page);
    this.page++;
    return stats;
  }

  fetchCareRecipientCards(): Observable<CareRecipientStatistics[][]> {
    this.careRecipientCardsLoaded = false;
    return this.api.getCareRecipientStatisticsCards(this.filters);
  }

  fetchActiveUsersStatistics(): Observable<IActiveUsersStats> {
    this.activeUsersTabDataLoaded = false;
    this.activeUsersStatistics = undefined;
    return this.api.getActiveUsersStats(this.filters);
  }

  calculateResponseTimes(alertResponses: IEmaacStatistics['responseTimes']['alertResponses']): IResponseTimeStatistics {
    const responseTimes = alertResponses ?? [];
    const responseTimeStats: IResponseTimeStatistics = {
      avgResTimeSeconds: 0,
      medianResTimeSeconds: 0,
    };

    if (responseTimes.length > 0) {
      const totalResponseTime = responseTimes.reduce(
        (acc, curr) => acc + curr.responseTime,
        0
      );
      responseTimeStats.avgResTimeSeconds =
        totalResponseTime / responseTimes.length;

      const sortedResponseTimes = responseTimes
        .map((res) => res.responseTime)
        .sort((a, b) => a - b);
      const mid = Math.floor(sortedResponseTimes.length / 2);
      responseTimeStats.medianResTimeSeconds =
        sortedResponseTimes.length % 2 === 0
          ? (sortedResponseTimes[mid - 1] + sortedResponseTimes[mid]) / 2
          : sortedResponseTimes[mid];
    }

    return responseTimeStats;
  }

  isTabDataLoaded(tab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers"): boolean {
    switch (tab) {
      case "alerts": return this.alertsTabDataLoaded;
      case "alarmCenter": return this.alarmCenterTabDataLoaded;
      case "careRecipients": return this.careRecipientCardsLoaded;
      case "activeUsers": return this.activeUsersTabDataLoaded;
      default: return false;
    }
  }

  isTabExportReady(tab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers"): boolean {
    switch (tab) {
      case "alerts":
        return this.alertsTabPagingComplete && !!this.alertStatistics;
      case "alarmCenter":
        return this.alarmCenterTabPagingComplete && !!this.alarmReasonTranslations && !!this.emaacStatistics;
      case "careRecipients":
        // Export must be gated on having at least one card in view, not merely on
        // the cards having loaded (an empty result still sets the loaded flag).
        return this.careRecipientCardsLoaded
          && !!this.careRecipientCards?.some((row) => row.length > 0);
      case "activeUsers":
        return this.activeUsersTabDataLoaded && !!this.activeUsersStatistics;
      default: return false;
    }
  }

  // ── Tab data cache ────────────────────────────────────────────────────
  // Maps each tab to the serialized filter state that was active when its
  // data last finished loading. Lets selectTab() skip the reload when
  // the filters have not changed since the tab was last visited.

  private tabCacheKeys = new Map<string, string>();

  isTabCacheValid(tab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers", filterKey: string): boolean {
    return this.tabCacheKeys.get(tab) === filterKey && this.isTabDataLoaded(tab);
  }

  setTabCacheKey(tab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers", filterKey: string): void {
    this.tabCacheKeys.set(tab, filterKey);
  }

  invalidateTabCache(tab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers"): void {
    this.tabCacheKeys.delete(tab);
  }

  exportAlertStatisticsJSON(): void {
    this.fileExporter.exportAlertStatisticsJSON(this.alertStatistics, this.filters);
  }

  exportEmaacStatisticsJSON(): void {
    this.fileExporter.exportEmaacStatisticsJSON(this.emaacStatistics, this.filters);
  }

  exportCareRecipientStatisticsJSON(): void {
    this.fileExporter.exportCareRecipientStatisticsJSON(this.careRecipientCards, this.filters);
  }

  exportActiveUsersStatisticsJSON(): void {
    this.fileExporter.exportActiveUsersStatisticsJSON(this.activeUsersStatistics, this.filters);
  }
  

  initPageCounter(): void {
    this.page = 0;
  }

  /**
   * Check if more cards are available to be added
   * @returns Available status and user card maximum limit
   */
  isCareRecipientCardsAvailable(): Observable<ICareRecipientCardUserLimit> {
    return this.api.checkCareRecipientStatisticsCardUserLimit();
  }

  /**
   * Request for adding note to care recipient statistics card
   * @param cardId ID of card / mongo document ID
   * @param note Note data
   * @returns True if note was added, else false
   */
  addNoteToCareRecipientCard(
    cardId: string,
    note: ICareRecipientCardNote
  ): Observable<boolean> {
    return this.api.addNoteToCareRecipientStatisticsCard(cardId, note);
  }

  /**
   * Request for adding new care recipient statistics card
   * @param config New care recipient statistics card to add
   * @returns True if add success, else false
   */
  createCareRecipientCard(
    config: CareRecipientCard
  ): Observable<boolean> {
    return this.api.createCareRecipientStatisticsCard(config);
  }

  /**
   * Request for updating care recipient statistics card
   * @param config Card update data
   * @returns True if update success, else false
   */
  updateCareRecipientCard(
    config: CareRecipientCard
  ): Observable<boolean> {
    return this.api.updateCareRecipientStatisticsCard(config);
  }

  /**
   * Request for deleting care recipient statistics card
   * @param cardId ID of card / mongo document ID
   * @returns True if delete success, else false
   */
  deleteCareRecipientCard(cardId: string): Observable<boolean> {
    return this.api.deleteCareRecipientStatisticsCard(cardId);
  }

  /**
   * Remove care recipient tab's message after delay
   */
  removeMessageTimer(): void {
    const time = 5000; // 5 seconds

    setTimeout(() => {
      this.careRecipientTabResultMsg = "";
    }, time);
  }

  /**
   * Fetch active users per day and per hour
   */

  getActiveUsersStats(filters: StatisticsFilters): Observable<IActiveUsersStats> {
    return this.api.getActiveUsersStats(filters);
  }

}
